import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner, Button } from '@kollektive/design-system';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { TENANT_STORAGE_KEY } from '@/context/AuthContext';
import { REGISTRATION_FLOW_KEY } from '@/routes/RegisterPage';
import styles from './AuthCallback.module.css';

const RETURN_URL_KEY = 'auth_return_url';

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the session from the URL (Supabase handles the PKCE exchange)
        const { data, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          setError(sessionError.message);
          return;
        }

        if (!data.session) {
          // If no session, try to exchange the code from the URL hash
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(
            window.location.href
          );

          if (exchangeError) {
            setError(exchangeError.message);
            return;
          }
        }

        // Check if this is a registration flow
        const flow = searchParams.get('flow');
        const isRegistrationFlow = flow === 'register' || sessionStorage.getItem(REGISTRATION_FLOW_KEY) === 'true';

        // Clear the registration flow flag
        sessionStorage.removeItem(REGISTRATION_FLOW_KEY);

        // If registration flow, redirect to setup page
        if (isRegistrationFlow) {
          navigate('/register/setup', { replace: true });
          return;
        }

        // Get tenant param from URL or session storage (for localhost development)
        let tenantParam = searchParams.get('tenant');
        if (!tenantParam) {
          tenantParam = sessionStorage.getItem(TENANT_STORAGE_KEY);
        }
        sessionStorage.removeItem(TENANT_STORAGE_KEY);

        // Get the return URL from session storage
        const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);
        sessionStorage.removeItem(RETURN_URL_KEY);

        // Build destination URL, preserving tenant param for localhost
        let destination = returnUrl || '/dashboard';
        const host = window.location.hostname;
        if (tenantParam && (host === 'localhost' || host === '127.0.0.1')) {
          const url = new URL(destination, window.location.origin);
          url.searchParams.set('tenant', tenantParam);
          destination = url.pathname + url.search;
        }

        // Redirect to the destination
        navigate(destination, { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      }
    };

    handleCallback();
  }, [navigate, searchParams]);

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContent}>
          <div className={styles.errorIcon}>
            <AlertTriangle size={48} />
          </div>
          <h1 className={styles.title}>Authentication Error</h1>
          <p className={styles.errorMessage}>{error}</p>
          <Button onClick={() => (window.location.href = '/login')}>Back to Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.spinnerContainer}>
          <Spinner size="lg" />
        </div>
        <p className={styles.text}>Processing authentication...</p>
      </div>
    </div>
  );
}

// Export the session storage key for use in ProtectedRoute
export { RETURN_URL_KEY };
